import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Item } from 'react-stately';
import {
  IconSlidersOutline,
} from '@saucelabs/ds-icons';
import { Filter } from '@saucelabs/ds-filters';
import { isEqual } from 'lodash';

import actions from '../../actions';
import constants from '../../constants';
import selectors from '../../selectors';
import { CONFIGURATION_TEST_TYPES } from '../../../../constants';
import { trackEvent, MANUAL_EVENTS } from '../../../../tracking';
import { FilterUpdate } from './DeviceFilterBar';

const {
  MOBILE_SELECTION_FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY: FILTER_CATEGORY,
} = constants;

const MiscFilter: React.FC = () => {
  const dispatch = useDispatch();
  const {
    activeFilters,
    testType,
  } = useSelector(selectors.mobileSelectionSelector);

  const isAppTest = testType === CONFIGURATION_TEST_TYPES.APP;

  const activeMiscFilterValues = useMemo(() => {
    let result: string[] = [];

    result = result.concat([...activeFilters.get(FILTER_CATEGORY.FORM_FACTOR)]);
    result = result.concat([...activeFilters.get(FILTER_CATEGORY.CLOUD_TYPE)]);
    result = result.concat([...activeFilters.get(FILTER_CATEGORY.NETWORK_TYPE)]);
    result = result.concat([...activeFilters.get(FILTER_CATEGORY.COMPATIBILITY)]);

    return result;
  }, [activeFilters]);

  const filterItems = useMemo(() => {
    let items: Array<React.ReactElement> = [];
    items = items.concat([
      (
        <Item
          key={MOBILE_SELECTION_FILTER.PHONE}
          textValue="Phones Only"
        >
          Phones Only
        </Item>
      ),
      (
        <Item
          key={MOBILE_SELECTION_FILTER.TABLET}
          textValue="Tablets Only"
        >
          Tablets Only
        </Item>
      ),
      (
        <Item
          key={MOBILE_SELECTION_FILTER.PRIVATE}
          textValue="Private Devices Only"
        >
          Private Devices Only
        </Item>
      ),
      (
        <Item
          key={MOBILE_SELECTION_FILTER.CARRIER}
          textValue="Carrier Connectivity"
        >
          Carrier Connectivity
        </Item>
      ),
    ]);

    if (isAppTest) {
      items.push(
        <Item
          key={MOBILE_SELECTION_FILTER.COMPATIBILITY}
          textValue="Compatible Only"
        >
          Compatible Only
        </Item>,
      );
    }

    return items;
  }, [isAppTest]);

  return (
    <Filter
      data-qa="device-filter-bar-misc-filters"
      label="Filters"
      name="miscFilters"
      onSelectionChange={([...newValues]) => {
        const newFilters: FilterUpdate = [];

        // Prepare form factor filters
        const selectedFormFactors: string[] = [];
        if (newValues.includes(MOBILE_SELECTION_FILTER.PHONE)) {
          selectedFormFactors.push(MOBILE_SELECTION_FILTER.PHONE);
        }
        if (newValues.includes(MOBILE_SELECTION_FILTER.TABLET)) {
          selectedFormFactors.push(MOBILE_SELECTION_FILTER.TABLET);
        }
        newFilters.push({
          category: FILTER_CATEGORY.FORM_FACTOR,
          values: selectedFormFactors,
        });
        // tracking event for FORM_FACTOR filter category
        if (!isEqual(selectedFormFactors, [...activeFilters.get(FILTER_CATEGORY.FORM_FACTOR)])) {
          trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
            filterCategory: FILTER_CATEGORY.FORM_FACTOR,
            filterValues: selectedFormFactors,
            testType,
          });
        }

        // Prepare private filter
        const selectedCloudTypes: string[] = [];
        if (newValues.includes(MOBILE_SELECTION_FILTER.PRIVATE)) {
          selectedCloudTypes.push(MOBILE_SELECTION_FILTER.PRIVATE);
        }
        newFilters.push({
          category: FILTER_CATEGORY.CLOUD_TYPE,
          values: selectedCloudTypes,
        });
        // tracking event for CLOUD_TYPE filter category
        if (!isEqual(selectedCloudTypes, [...activeFilters.get(FILTER_CATEGORY.CLOUD_TYPE)])) {
          trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
            filterCategory: FILTER_CATEGORY.CLOUD_TYPE,
            filterValues: selectedCloudTypes,
            testType,
          });
        }

        // Prepare network type filter
        const selectedNetworkTypes: string[] = [];
        if (newValues.includes(MOBILE_SELECTION_FILTER.CARRIER)) {
          selectedNetworkTypes.push(MOBILE_SELECTION_FILTER.CARRIER);
        }
        newFilters.push({
          category: FILTER_CATEGORY.NETWORK_TYPE,
          values: selectedNetworkTypes,
        });
        // tracking event for NETWORK_TYPE filter category
        if (!isEqual(selectedNetworkTypes, [...activeFilters.get(FILTER_CATEGORY.NETWORK_TYPE)])) {
          trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
            filterCategory: FILTER_CATEGORY.NETWORK_TYPE,
            filterValues: selectedNetworkTypes,
            testType,
          });
        }

        // Prepare compatibility filter
        const selectedCompatabilities: string[] = [];
        if (newValues.includes(MOBILE_SELECTION_FILTER.COMPATIBILITY)) {
          selectedCompatabilities.push(MOBILE_SELECTION_FILTER.COMPATIBILITY);
        }
        newFilters.push({
          category: FILTER_CATEGORY.COMPATIBILITY,
          values: selectedCompatabilities,
        });
        // tracking event for COMPATIBLITY filter category
        if (
          !isEqual(selectedCompatabilities, [...activeFilters.get(FILTER_CATEGORY.COMPATIBILITY)])
        ) {
          trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
            filterCategory: FILTER_CATEGORY.COMPATIBILITY,
            filterValues: selectedCompatabilities,
            testType,
          });
        }

        dispatch(
          actions.setFilters(newFilters));
      }}
      prefixIcon={IconSlidersOutline}
      selectedKeys={activeMiscFilterValues}
      selectionMode="multiple"
    >
      {filterItems}
    </Filter>
  );
};

export default MiscFilter;
